import { Link } from "react-router-dom";
import LogoMark from "../components/Logo";

const SECTIONS = [
  {
    h: "1. Who we are",
    p: [
      "Aria (\"Aria\", \"we\", \"us\", \"our\") is an AI business transformation service operated by Guru Biz Solutions, Hyderabad, India. We build conversational AI, predictive systems, and WhatsApp-based automations for Indian small and mid-sized businesses.",
      "Our principal place of business is Hyderabad, Telangana, India. You can reach us at start@aria4u.com or on WhatsApp at +91 79954 95312.",
    ],
  },
  {
    h: "2. What information we collect",
    p: [
      "Information you give us directly: your name, business name, WhatsApp number, email, city, industry, and the messages you send to our AI assistant. If you fill out a form or request a demo, we collect what you provide there.",
      "Information collected automatically: device type, browser, IP address, pages visited on aria4u.com, referral source, and approximate location (derived from IP). We use this for analytics and to improve the site.",
      "Information from our AI assistant: the conversation transcripts you share with Aria on WhatsApp or in our website chat. We store these so the AI can give you continuous, context-aware advice — and so we can improve the service.",
      "Information from third-party platforms (only with your consent): if you connect your Google Business, Practo, Tally, or similar tools to Aria, we receive data from those platforms strictly to power the features you have asked for.",
    ],
  },
  {
    h: "3. How we use your information",
    p: [
      "To run the AI conversation and respond to you on WhatsApp, email, or our live chat.",
      "To prepare your business diagnosis, gap analysis, 10-year vision and pricing proposals.",
      "To build, deploy and maintain the AI systems you commission from us (e.g., WhatsApp bots, prediction engines, dashboards).",
      "To communicate with you about your account, billing, and service updates.",
      "To improve Aria — for product analytics, debugging, and AI model evaluation (using anonymised or aggregated data wherever practical).",
      "To comply with legal obligations under Indian law, including the Digital Personal Data Protection Act, 2023 (DPDP Act).",
    ],
  },
  {
    h: "4. Where your data lives",
    p: [
      "Your data is stored on secure cloud infrastructure (primarily MongoDB Atlas and managed Vercel / Railway hosting) with encryption at rest and in transit.",
      "Some processing happens via trusted third-party AI providers — including Anthropic (Claude), OpenAI and Google (Gemini) — which may process your messages in their own data regions. These providers do not train their general-purpose models on your data when used through our paid API integrations.",
      "WhatsApp messages are routed through Meta's WhatsApp Cloud API.",
    ],
  },
  {
    h: "5. Who we share data with",
    p: [
      "We do not sell your personal data. Ever.",
      "We share data only with: (a) our cloud and AI infrastructure providers strictly to run the service, (b) payment processors when you subscribe, (c) integration partners you have authorised (Google Business, Practo, Tally, etc.), and (d) Indian regulators or courts when legally required.",
    ],
  },
  {
    h: "6. How long we keep your data",
    p: [
      "We retain your conversation history and business profile for as long as you are an active client, and for up to 24 months after you stop using Aria — so we can support you if you return, defend ourselves in legal disputes, and meet tax obligations.",
      "You can ask us to delete your data at any time. See your rights below.",
    ],
  },
  {
    h: "7. Your rights",
    p: [
      "Under the DPDP Act 2023 you have the right to: access the personal data we hold about you, correct inaccurate data, request deletion (\"right to be forgotten\"), nominate someone to act on your behalf, and raise a grievance with us before approaching the Data Protection Board of India.",
      "To exercise any of these rights, email start@aria4u.com with the subject \"DPDP Request\". We respond within 30 days.",
    ],
  },
  {
    h: "8. Cookies and analytics",
    p: [
      "aria4u.com uses minimal cookies — only for keeping you logged in (if you have an account) and basic analytics. We do not use third-party advertising cookies. You can disable cookies in your browser without breaking the site.",
    ],
  },
  {
    h: "9. Children",
    p: [
      "Aria is built for business owners. We do not knowingly collect data from anyone under 18. If a parent or guardian becomes aware that a child has shared personal data with us, please write to us and we will delete it.",
    ],
  },
  {
    h: "10. Security",
    p: [
      "We protect your data with industry-standard measures — TLS encryption in transit, encrypted storage at rest, scoped API keys, access logs, and least-privilege access for our team. No system on the internet is perfectly secure, but we take this seriously.",
    ],
  },
  {
    h: "11. Changes to this policy",
    p: [
      "We may update this policy as our service evolves. The latest version always lives at aria4u.com/privacy with the \"last updated\" date below. Material changes will be communicated to active clients on WhatsApp or by email.",
    ],
  },
  {
    h: "12. Grievance officer",
    p: [
      "Krishna Eluri, Grievance Officer · Guru Biz Solutions, Hyderabad, India · start@aria4u.com · +91 79954 95312. Grievances are acknowledged within 48 hours and resolved within 30 days.",
    ],
  },
];

const PrivacyPolicy = () => (
  <div className="legal-page" data-testid="privacy-page">
    <header className="legal-nav">
      <div className="legal-nav-inner">
        <Link to="/" className="nav-brand" data-testid="legal-back-home">
          <LogoMark size={32} />
          <span className="nav-logo">Aria<span className="dot">.</span></span>
        </Link>
        <Link to="/" className="legal-back">← Back to home</Link>
      </div>
    </header>

    <main className="legal-main">
      <div className="legal-hero">
        <span className="eyebrow">Legal · Privacy</span>
        <h1 className="legal-h1">
          Your data.<br />
          <span className="serif">Your business.</span>
        </h1>
        <p className="legal-sub">
          We're an AI company. That means we take privacy seriously — because
          you trust us with the heartbeat of your business. Here's exactly
          what we collect, why, and what you can do about it.
        </p>
        <p className="legal-meta">Last updated: December 2025 · Governing law: India (DPDP Act, 2023)</p>
      </div>

      <div className="legal-body">
        {SECTIONS.map((s, i) => (
          <section key={i} className="legal-section" data-testid={`privacy-section-${i}`}>
            <h2 className="legal-h2">{s.h}</h2>
            {s.p.map((para, j) => (
              <p key={j} className="legal-p">{para}</p>
            ))}
          </section>
        ))}

        <section className="legal-section legal-cta-box">
          <h2 className="legal-h2">Questions?</h2>
          <p className="legal-p">
            Email us at <a href="mailto:start@aria4u.com">start@aria4u.com</a> or
            WhatsApp <a href="https://wa.me/917995495312" target="_blank" rel="noreferrer">+91 79954 95312</a>.
            We answer every message — usually within a few hours.
          </p>
        </section>
      </div>
    </main>

    <footer className="legal-footer">
      <div className="legal-footer-inner">
        <Link to="/" className="nav-brand">
          <LogoMark size={28} />
          <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: 22 }}>
            Aria<span style={{ color: "#d4a017" }}>.</span>
          </span>
        </Link>
        <p>© 2025 Aria · Hyderabad, India · Built for 2036. Available today.</p>
      </div>
    </footer>
  </div>
);

export default PrivacyPolicy;
